<template>
  <div
    class="base-dropdown"
    ref="dropdown"
    :class="{
      'active': isActive || isHovered,
      'base-dropdown_wide': !autoWidth,
    }"
  >
    <div
      class="base-dropdown__overlay"
      v-if="!hideOverlay"
    ></div>
    <div
      class="base-dropdown__block"
      ref="block"
    >
      <div class="base-dropdown__container">
        <div class="base-dropdown__menu">
          <slot name="items"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core/lib/popper-lite';
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import flip from '@popperjs/core/lib/modifiers/flip';

export default {
  name: 'Dropdown',
  props: {
    trigger: String,
    triggerId: String,
    listeners: {
      type: String,
      default: 'click',
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    hideOverlay: Boolean,
    noListeners: Boolean,
    usePopper: {
      type: Boolean,
      default: true,
    },
    autoWidth: Boolean,
    show: Boolean,
  },
  data() {
    return {
      isActive: false,
      isHovered: false,
      hoverTimeout: null,
      unhoverTimeout: null,
      triggerEl: null,
      dropdown: null,
    };
  },
  computed: {
    isShown() {
      return this.isActive || this.isHovered || this.$props.show === true;
    },
  },
  methods: {
    open(e) {
      this.preventEvent(e);
      this.isActive = true;
      this.resetHover();
      this.$emit('toggle', true);
    },
    close() {
      this.isActive = false;
      this.resetHover();
      this.$emit('toggle', false);
    },
    toggle(e) {
      this.preventEvent(e);
      this.isActive = !this.isActive;
      this.resetHover();
      const delay = this.isActive ? 10 : 0;
      setTimeout(() => {
        this.$emit('toggle', this.isActive);
      }, delay);
    },
    preventEvent(e) {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
    },
    resetHover() {
      this.isHovered = false;
      clearTimeout(this.hoverTimeout);
      clearTimeout(this.unhoverTimeout);
    },
    hover() {
      if (!this.isHovered) {
        this.hoverTimeout = setTimeout(() => {
          this.isHovered = true;
        }, 300);
      }
      clearTimeout(this.unhoverTimeout);
    },
    unhover() {
      if (this.isHovered) {
        this.unhoverTimeout = setTimeout(() => {
          this.isHovered = false;
        }, 600);
      }
      clearTimeout(this.hoverTimeout);
    },
    setPosition() {
      this.popperInstance = createPopper(this.triggerEl, this.dropdownBlock, {
        placement: this.placement,
        modifiers: [preventOverflow, flip],
      });
    },
    checkClick(e) {
      if (!this.triggerEl.contains(e.target) && this.triggerEl !== document.activeElement) {
        this.close();
      }
    },
    defineListeners() {
      const vm = this;
      // Click
      if (this.listeners.indexOf('click') !== -1) {
        if (this.triggerEl) this.triggerEl.addEventListener('click', vm.toggle);
        if (this.dropdown) this.dropdown.addEventListener('click', vm.close);
      }
      // Hover
      if (this.listeners.indexOf('hover') !== -1 && !this.$root.isTouch) {
        if (this.triggerEl) {
          this.triggerEl.addEventListener('mouseenter', this.hover);
          this.triggerEl.addEventListener('mouseleave', this.unhover);
        }
        if (this.dropdown) {
          this.dropdown.addEventListener('mouseenter', this.hover);
          this.dropdown.addEventListener('mouseleave', this.unhover);
        }
      }
      // Focus
      if (this.listeners.indexOf('focus') !== -1) {
        if (this.triggerEl) this.triggerEl.addEventListener('focus', this.open);
      }
    },
    getRefComponent(parent) {
      return parent && parent.$refs[this.trigger];
    },
    getTriggerEl() {
      if (this.triggerId) {
        return document.getElementById(this.triggerId);
      }
      const ref = this.getRefComponent(this.$parent);
      if (ref && ref.$el) {
        return ref.$el;
      }
      return ref;
    },
  },
  watch: {
    isShown(val) {
      if (!this.noListeners) {
        document.removeEventListener('click', this.checkClick);
      }
      if (val) {
        if (this.usePopper) {
          this.setPosition();
        }
        if (!this.noListeners) {
          document.addEventListener('click', this.checkClick);
        }
        this.triggerEl.classList.add('has-dropdown');
        return;
      }
      if (this.$props.show !== true) {
        this.triggerEl.classList.remove('has-dropdown');
        if (!val && this.popperInstance) {
          this.popperInstance.destroy();
          this.popperInstance = null;
        }
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.triggerEl = this.getTriggerEl();
      this.dropdown = this.$refs.dropdown;
      this.dropdownBlock = this.$refs.block;
      if (!this.noListeners) {
        this.defineListeners();
      }
    }, 1000);
  },
  beforeDestroy() {
    if (!this.noListeners) {
      if (this.triggerEl) {
        this.triggerEl.removeEventListener('click', this.toggle);
        this.triggerEl.removeEventListener('mouseenter', this.hover);
        this.triggerEl.removeEventListener('mouseleave', this.unhover);
      }
      if (this.dropdown) {
        this.dropdown.removeEventListener('click', this.close);
        this.dropdown.removeEventListener('mouseenter', this.hover);
        this.dropdown.removeEventListener('mouseleave', this.unhover);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
  @import "./Dropdown";
</style>
